/**
 * TokenCounter handles tracking and recording token usage for different LLM operations
 * in chat conversations and streaming completions.
 * This version uses character-based estimation for all providers, removing js-tiktoken dependency.
 */
export class TokenCounter {
  constructor(model = "gpt-4.1-mini", provider = "openai") {
    this.model = model;
    this.provider = provider.toLowerCase();
    this.promptTokens = 0;
    this.completionTokens = 0;
    this.totalTokens = 0;
    this.imageOutputTokens = 0;
    this.trackingEnabled = true;
  }

  /**
   * Estimate tokens using character-based approximation with enhanced accuracy
   * @param {string} text - Text to estimate tokens for
   * @returns {number} - Estimated token count
   */
  estimateTokens(text) {
    if (!text || typeof text !== "string") return 0;

    // Enhanced token estimation that considers word boundaries and common patterns
    const baseCharacterRatios = {
      anthropic: 3.5, // Claude models: ~3.5 chars per token
      google: 4.0, // Gemini models: ~4 chars per token
      gemini: 4.0, // Gemini models: ~4 chars per token
      cohere: 4.2, // Cohere models: ~4.2 chars per token
      mistral: 3.8, // Mistral models: ~3.8 chars per token
      groq: 3.5, // Groq models: ~3.5 chars per token
      xai: 3.5, // xAI models: ~3.5 chars per token
      openai: 3.5, // OpenAI models: ~3.5 chars per token
      openrouter: 3.5, // OpenRouter: ~3.5 chars per token
    };

    const baseRatio = baseCharacterRatios[this.provider] || 4.0;

    // Enhanced estimation considering text characteristics
    let adjustedRatio = baseRatio;

    // Adjust for common patterns that affect tokenization
    const wordCount = text.split(/\s+/).length;
    const avgWordLength = text.length / wordCount;

    // Shorter words tend to be more tokens per character
    if (avgWordLength < 4) {
      adjustedRatio *= 0.9; // More tokens per character for short words
    } else if (avgWordLength > 8) {
      adjustedRatio *= 1.1; // Fewer tokens per character for long words
    }

    // Account for special characters and punctuation
    const specialCharCount = (text.match(/[^\w\s]/g) || []).length;
    const specialCharRatio = specialCharCount / text.length;
    if (specialCharRatio > 0.1) {
      adjustedRatio *= 0.95; // More tokens for text with many special characters
    }

    // Account for code-like content (many symbols, camelCase, etc.)
    const codePatterns = /[\{\}\[\]\(\)<>]/g; // eslint-disable-line no-useless-escape
    const codeCharCount = (text.match(codePatterns) || []).length;
    if (codeCharCount > wordCount * 0.1) {
      adjustedRatio *= 0.9; // Code tends to have more tokens per character
    }

    const estimatedTokens = Math.ceil(text.length / adjustedRatio);
    return Math.max(1, estimatedTokens); // Ensure at least 1 token for non-empty text
  }

  /**
   * Count tokens using character-based estimation
   * @param {string} text - Text to count tokens for
   * @returns {number} - Token count
   */
  countTokens(text) {
    if (!text || typeof text !== "string") return 0;
    return this.estimateTokens(text);
  }

  /**
   * Calculate estimated image output tokens for image generation models based on quality and size
   * @param {string} size - Image size (e.g., "1024x1024", "1024x1536", "1536x1024")
   * @param {string} quality - Image quality ("low", "medium", "high")
   * @returns {number} - Estimated image output tokens
   */
  calculateImageTokens(size = "1024x1024", quality = "medium") {
    // Token calculations based on common image generation pricing structures
    const tokenMap = {
      "256x256": {
        low: 68,
        medium: 264,
        high: 1040,
      },
      "512x512": {
        low: 136,
        medium: 528,
        high: 2080,
      },
      "1024x1024": {
        low: 272,
        medium: 1056,
        high: 4160,
      },
      "1024x1536": {
        low: 408,
        medium: 1584,
        high: 6240,
      },
      "1536x1024": {
        low: 400,
        medium: 1568,
        high: 6208,
      },
      "1792x1024": {
        low: 488,
        medium: 1896,
        high: 7472,
      },
      "1024x1792": {
        low: 488,
        medium: 1896,
        high: 7472,
      },
    };

    // Default to medium quality 1024x1024 if not found
    const sizeTokens = tokenMap[size] || tokenMap["1024x1024"];
    const tokens = sizeTokens[quality] || sizeTokens.medium;

    return tokens;
  }

  /**
   * Add image output tokens to the counter
   * @param {string} size - Image size
   * @param {string} quality - Image quality
   * @returns {number} - Image tokens added
   */
  addImageTokens(size, quality) {
    if (!this.trackingEnabled) return 0;

    try {
      const imageTokens = this.calculateImageTokens(size, quality);
      this.imageOutputTokens += imageTokens;
      this.totalTokens =
        this.promptTokens + this.completionTokens + this.imageOutputTokens;

      return imageTokens;
    } catch (error) {
      console.error("Error adding image tokens:", error);
      return 0;
    }
  }

  /**
   * Count tokens in prompt messages with enhanced estimation for multimodal content
   * @param {Array} messages - Array of message objects
   * @returns {number} - Estimated prompt token count
   */
  countPromptTokens(messages) {
    if (!this.trackingEnabled || !messages || !Array.isArray(messages))
      return 0;

    try {
      let tokens = 0;

      // Count tokens for each message
      for (const message of messages) {
        // Count content tokens
        if (typeof message.content === "string") {
          tokens += this.countTokens(message.content);
        } else if (Array.isArray(message.content)) {
          // Handle array content (multimodal)
          for (const contentPart of message.content) {
            if (contentPart.type === "text" && contentPart.text) {
              tokens += this.countTokens(contentPart.text);
            } else if (
              contentPart.type === "image_url" ||
              contentPart.type === "image"
            ) {
              // Rough estimation for image input tokens (varies by provider)
              tokens += this.estimateImageInputTokens(contentPart);
            }
          }
        }

        // Add tokens for role and message formatting
        if (message.role) {
          tokens += this.countTokens(message.role);
        }

        // Add overhead tokens for message structure
        tokens += 4; // ~4 tokens for message formatting overhead
      }

      // Additional tokens for request formatting and system overhead
      tokens += 3; // ~3 tokens for the overall request formatting

      this.promptTokens = tokens;
      this.totalTokens =
        this.promptTokens + this.completionTokens + this.imageOutputTokens;

      return tokens;
    } catch (error) {
      console.error("Error counting prompt tokens:", error);
      return 0;
    }
  }

  /**
   * Estimate tokens for image input content
   * @param {Object} imageContent - Image content object
   * @returns {number} - Estimated token count for image
   */
  estimateImageInputTokens(imageContent) {
    // Basic estimation for image input tokens
    // This varies significantly by provider and image resolution
    const baseImageTokens = {
      anthropic: 1568, // Claude's typical image token cost
      openai: 765, // GPT-4V average for standard images
      google: 1000, // Gemini Pro Vision estimate
      gemini: 1000,
    };

    const baseTokens = baseImageTokens[this.provider] || 1000;

    // Try to adjust based on image details if available
    if (imageContent.image_url?.detail === "high") {
      return Math.floor(baseTokens * 1.5);
    } else if (imageContent.image_url?.detail === "low") {
      return Math.floor(baseTokens * 0.5);
    }

    return baseTokens;
  }

  /**
   * Count completion tokens with streaming support
   * @param {string} text - Completion text to count
   * @returns {number} - Token count for this text chunk
   */
  countCompletionTokens(text) {
    if (!this.trackingEnabled || !text) return 0;

    try {
      const tokens = this.countTokens(text);
      this.completionTokens += tokens;
      this.totalTokens =
        this.promptTokens + this.completionTokens + this.imageOutputTokens;

      return tokens;
    } catch (error) {
      console.error("Error counting completion tokens:", error);
      return 0;
    }
  }

  /**
   * Set token counts from provider response (for providers that return usage info)
   * @param {Object} usage - Usage object from provider response
   * @param {number} usage.prompt_tokens - Prompt tokens
   * @param {number} usage.completion_tokens - Completion tokens
   * @param {number} usage.total_tokens - Total tokens
   * @param {number} usage.input_tokens - Input tokens (Anthropic format)
   * @param {number} usage.output_tokens - Output tokens (Anthropic format)
   */
  setTokensFromUsage(usage) {
    if (!usage || !this.trackingEnabled) return;

    try {
      // Handle OpenAI format
      if (
        usage.prompt_tokens !== undefined &&
        usage.completion_tokens !== undefined
      ) {
        this.promptTokens = usage.prompt_tokens;
        this.completionTokens = usage.completion_tokens;
        this.totalTokens =
          usage.total_tokens || this.promptTokens + this.completionTokens;
        return;
      }

      // Handle Anthropic format
      if (
        usage.input_tokens !== undefined &&
        usage.output_tokens !== undefined
      ) {
        this.promptTokens = usage.input_tokens;
        this.completionTokens = usage.output_tokens;
        this.totalTokens = this.promptTokens + this.completionTokens;
        return;
      }

      // Handle Google/Gemini format
      if (
        usage.promptTokenCount !== undefined &&
        usage.candidatesTokenCount !== undefined
      ) {
        this.promptTokens = usage.promptTokenCount;
        this.completionTokens = usage.candidatesTokenCount;
        this.totalTokens =
          usage.totalTokenCount || this.promptTokens + this.completionTokens;
        return;
      }

      // Handle other formats with total_tokens
      if (usage.total_tokens !== undefined) {
        this.totalTokens = usage.total_tokens;
        // If we don't have breakdown, estimate 70% prompt, 30% completion
        if (this.promptTokens === 0 && this.completionTokens === 0) {
          this.promptTokens = Math.floor(this.totalTokens * 0.7);
          this.completionTokens = this.totalTokens - this.promptTokens;
        }
      }
    } catch (error) {
      console.error("Error setting tokens from usage:", error);
    }
  }

  /**
   * Record token usage to database
   * @param {string} userId - User ID
   * @param {string} workspaceId - Workspace ID
   * @param {string} modelId - Model ID (optional, defaults to this.model)
   * @param {string} featureId - Feature ID (default: "chat")
   * @returns {Promise} - Promise resolving to record result
   */
  async recordTokenUsage(userId, workspaceId, modelId, featureId = "chat") {
    if (!this.trackingEnabled || this.totalTokens === 0) {
      return;
    }

    if (!userId) {
      console.error("Cannot record token usage: Missing userId");
      return;
    }

    try {
      // Import the token stats module as default export
      const userTokenStatsModule = await import(
        "@/lib/db/utils/supabase/userTokenStats"
      );
      const userTokenStats = userTokenStatsModule.default;

      // Access the create function through the imported object
      const result = await userTokenStats.create({
        userId,
        workspaceId,
        promptTokens: this.promptTokens,
        completionTokens: this.completionTokens,
        totalTokens: this.totalTokens,
        modelId: modelId || this.model,
        featureId,
        metadata: {
          imageOutputTokens: this.imageOutputTokens,
          tracked_via: "streaming",
          provider: this.provider,
          estimation_method: "character_ratio_enhanced",
          timestamp: new Date().toISOString(),
          character_ratios_used: this.getProviderRatios(),
        },
      });

      return result;
    } catch (error) {
      console.error(
        `Failed to record token usage for ${this.provider}:`,
        error
      );
      return null;
    }
  }

  /**
   * Get provider-specific character ratios for metadata
   * @returns {Object} - Object containing ratio information
   */
  getProviderRatios() {
    const ratios = {
      anthropic: 3.5,
      google: 4.0,
      gemini: 4.0,
      cohere: 4.2,
      mistral: 3.8,
      groq: 3.5,
      xai: 3.5,
      openai: 3.5,
      openrouter: 3.5,
    };

    return {
      provider: this.provider,
      base_ratio: ratios[this.provider] || 4.0,
      method: "enhanced_character_estimation",
    };
  }

  /**
   * Enable or disable token tracking
   * @param {boolean} enabled - Whether to enable tracking
   */
  setTrackingEnabled(enabled) {
    this.trackingEnabled = Boolean(enabled);
  }

  /**
   * Reset all token counters
   */
  reset() {
    this.promptTokens = 0;
    this.completionTokens = 0;
    this.imageOutputTokens = 0;
    this.totalTokens = 0;
  }

  /**
   * Get current token counts with additional metadata
   * @returns {Object} - Current token counts and metadata
   */
  getTokenCounts() {
    return {
      promptTokens: this.promptTokens,
      completionTokens: this.completionTokens,
      imageOutputTokens: this.imageOutputTokens,
      totalTokens: this.totalTokens,
      provider: this.provider,
      model: this.model,
      trackingEnabled: this.trackingEnabled,
      estimationMethod: "character_ratio_enhanced",
    };
  }

  /**
   * Get a summary of token usage for reporting
   * @returns {Object} - Token usage summary
   */
  getUsageSummary() {
    const total = this.totalTokens;
    const promptPercent =
      total > 0 ? ((this.promptTokens / total) * 100).toFixed(1) : 0;
    const completionPercent =
      total > 0 ? ((this.completionTokens / total) * 100).toFixed(1) : 0;
    const imagePercent =
      total > 0 ? ((this.imageOutputTokens / total) * 100).toFixed(1) : 0;

    return {
      provider: this.provider,
      model: this.model,
      totalTokens: total,
      breakdown: {
        prompt: { tokens: this.promptTokens, percentage: `${promptPercent}%` },
        completion: {
          tokens: this.completionTokens,
          percentage: `${completionPercent}%`,
        },
        images: {
          tokens: this.imageOutputTokens,
          percentage: `${imagePercent}%`,
        },
      },
      estimationMethod: "character_ratio_enhanced",
    };
  }
}

export default TokenCounter;
